import React, { useState, useEffect }  from 'react';
import { Card, Row, Col, Statistic, List, Tooltip, Badge, Button, Space, Tag, Alert } from 'antd';
import {
  HomeOutlined,
  FunnelPlotFilled,
  SunFilled,
  ReloadOutlined,
  SwapOutlined,
  ShoppingOutlined,
  DeleteFilled,
} from '@ant-design/icons';
import moment from 'moment';
import { useHistory } from 'react-router-dom';

import * as ToolRequest from '../../tools/ToolRequest';
import * as ToolMath from '../../tools/ToolMath';

import HomeSiteCardStationListItem from './HomeSiteCardStationListItem';
import HomeSiteCardProductionDetailModal from './HomeSiteCardProductionDetailModal';
import HomeSiteCardPipelineVisual from './HomeSiteCardPipelineVisual';
import SpinFull from '../../components/SpinFull';
import DayCountDisplay from '../../components/DayCountDisplay';
import CropNameBadge from '../../components/CropNameBadge';

const tip = (label, title) => <Tooltip title={title}><span>{label}</span></Tooltip>;

const isLocalSource = (record) => record.source !== 'EXTERNAL';

const formatPercent = (numerator, denominator) => {
  if (!denominator || denominator <= 0) {
    return '-';
  }
  return `${((numerator / denominator) * 100).toFixed(1)}%`;
};

const sortSummaryByReady = (summary, crops, targetField) => {
  return [...summary].sort((a, b) => {
    const aReady = a.crops.some(crop => crops[crop.id] && ToolMath.daysFromNowNumber(a.date) >= crops[crop.id][targetField]);
    const bReady = b.crops.some(crop => crops[crop.id] && ToolMath.daysFromNowNumber(b.date) >= crops[crop.id][targetField]);
    if (aReady !== bReady) {
      return aReady ? -1 : 1;
    }
    return a.date.localeCompare(b.date);
  }).map(item => ({
    ...item,
    crops: [...item.crops].sort((a, b) => {
      const aReady = crops[a.id] && ToolMath.daysFromNowNumber(item.date) >= crops[a.id][targetField] ? 0 : 1;
      const bReady = crops[b.id] && ToolMath.daysFromNowNumber(item.date) >= crops[b.id][targetField] ? 0 : 1;
      if (aReady !== bReady) {
        return aReady - bReady;
      }
      return 0;
    }),
  }));
};

export default function HomeSiteCard(props) {
  const { site, title } = props;
  const history = useHistory();

  const [loaded, setLoaded] = useState(false);
  const [cardKey, setCardKey] = useState(Math.random().toString());
  const [stats, setStats] = useState(null);
  const [propagationSummary, setPropagationSummary] = useState(null);
  const [productionSummary, setProductionSummary] = useState(null);
  const [stations, setStations] = useState(null);
  const [crops, setCrops] = useState({});
  const [dashboardExtras, setDashboardExtras] = useState(null);

  const [detailModal, setDetailModal] = useState(null);

  const getStageStatus = (daysElapsed, targetDays) => {
    const remaining = targetDays - daysElapsed;
    if (remaining <= 0) {
      return { tag: 'Ready', color: 'green' };
    }
    return { tag: `${remaining}d left`, color: 'blue' };
  };

  const quickLogPath = (action, extraParams = {}) => {
    const params = new URLSearchParams({ siteId: site.id });
    for (let key of Object.keys(extraParams)) {
      if (extraParams[key] != null && extraParams[key] !== '') {
        params.set(key, extraParams[key]);
      }
    }
    return `/farm-production-record/add/${action}?${params.toString()}`;
  };

  const wasteLogPath = (leftovers, focusItem) => {
    const entries = leftovers.map(item => ({
      cropId: item.cropId,
      amount: item.amount,
      stage: item.wasteStage,
    }));
    return quickLogPath('waste', {
      recordedAt: focusItem.recordedAt || focusItem.date,
      entries: JSON.stringify(entries),
    });
  };

  useEffect(() => {
    setLoaded(false);

    (async () => {
      const stats = await ToolRequest.request('GET', `/v1/farm-site/${site.id}/stats`);
      setStats(stats);

      const propagationSummaryRes = await ToolRequest.request('GET', `/v1/farm-site/${site.id}/propagation-summary`);
      const productionSummaryRes = await ToolRequest.request('GET', `/v1/farm-site/${site.id}/production-summary`);

      const harvestWeekRes = await ToolRequest.request('GET', '/v1/farm-production-record', {
        siteId: site.id,
        type: 'HARVEST',
        beginAt: moment().subtract(7, 'days').startOf('day').toISOString(),
        count: 1000,
      });

      const allRecordsRes = await ToolRequest.request('GET', '/v1/farm-production-record', {
        siteId: site.id,
        count: 1000,
      });

      const enabledCropsRes = await ToolRequest.request('GET', '/v1/farm-crop', {
        companyId: site.companyId,
        isEnabled: true,
        count: 1000,
      });

      const cropMap = {};
      for (let crop of enabledCropsRes.data) {
        cropMap[crop.id] = crop;
      }

      let cropIds = [];
      for (let summary of propagationSummaryRes.data) {
        cropIds = [...cropIds, ...summary.crops.map(crop => crop.id)];
      }
      for (let summary of productionSummaryRes.data) {
        cropIds = [...cropIds, ...summary.crops.map(crop => crop.id)];
      }
      cropIds = cropIds.filter((value, index, self) => self.indexOf(value) === index);

      for (let cropId of cropIds) {
        if (!cropMap[cropId]) {
          cropMap[cropId] = await ToolRequest.request('GET', `/v1/farm-crop/${cropId}`);
        }
      }

      setCrops(cropMap);

      const sortedPropagationSummary = sortSummaryByReady(propagationSummaryRes.data, cropMap, 'seedlingDayCount');
      const sortedProductionSummary = sortSummaryByReady(productionSummaryRes.data, cropMap, 'plantingDayCount');
      setPropagationSummary(sortedPropagationSummary);
      setProductionSummary(sortedProductionSummary);

      const harvestWeek = {
        amount: harvestWeekRes.data.reduce((sum, record) => sum + record.amount, 0),
        weight: harvestWeekRes.data.reduce((sum, record) => sum + (record.weight || 0), 0),
      };

      const byCrop = {};
      for (let crop of enabledCropsRes.data) {
        byCrop[crop.id] = {
          cropId: crop.id,
          name: crop.name,
          sow: 0,
          localTransplant: 0,
          transplant: 0,
          wastePropagation: 0,
          wasteProduction: 0,
          harvest: 0,
          harvestWeek: 0,
        };
      }
      for (let record of allRecordsRes.data) {
        if (!byCrop[record.cropId]) {
          continue;
        }
        if (record.type === 'SOW') {
          byCrop[record.cropId].sow += record.amount;
        } else if (record.type === 'TRANSPLANT') {
          byCrop[record.cropId].transplant += record.amount;
          if (isLocalSource(record)) {
            byCrop[record.cropId].localTransplant += record.amount;
          }
        } else if (record.type === 'WASTE') {
          if (record.stage === 'PROPAGATION') {
            byCrop[record.cropId].wastePropagation += record.amount;
          } else if (record.stage === 'PRODUCTION') {
            byCrop[record.cropId].wasteProduction += record.amount;
          }
        } else if (record.type === 'HARVEST') {
          byCrop[record.cropId].harvest += record.amount;
        }
      }
      for (let record of harvestWeekRes.data) {
        if (byCrop[record.cropId]) {
          byCrop[record.cropId].harvestWeek += record.amount;
        }
      }

      const cropPipeline = Object.values(byCrop).map(row => ({
        ...row,
        inPropagation: row.sow - row.localTransplant - row.wastePropagation,
        inProduction: row.transplant - row.harvest - row.wasteProduction,
      }));

      const todayActions = [];
      const leftoverActions = [];
      for (let item of propagationSummaryRes.data) {
        for (let cropItem of item.crops) {
          const crop = cropMap[cropItem.id];
          if (!crop) {
            continue;
          }
          const daysElapsed = ToolMath.daysFromNowNumber(item.date);
          if (daysElapsed < crop.seedlingDayCount) {
            continue;
          }
          leftoverActions.push({
            key: `prop-left-${item.date}-${cropItem.id}`,
            stageLabel: 'Propagation',
            wasteStage: 'PROPAGATION',
            cropName: crop.name,
            date: item.date,
            recordedAt: cropItem.referenceRecordedAt || item.date,
            amount: cropItem.amount,
            originalAmount: cropItem.originalAmount,
            cropId: cropItem.id,
            type: 'SOW',
            typeLabel: 'Sowing',
          });
        }
      }
      for (let item of productionSummaryRes.data) {
        for (let cropItem of item.crops) {
          const crop = cropMap[cropItem.id];
          if (!crop) {
            continue;
          }
          const daysElapsed = ToolMath.daysFromNowNumber(item.date);
          if (daysElapsed < crop.plantingDayCount) {
            continue;
          }
          leftoverActions.push({
            key: `prod-left-${item.date}-${cropItem.id}`,
            stageLabel: 'Production',
            wasteStage: 'PRODUCTION',
            cropName: crop.name,
            date: item.date,
            recordedAt: cropItem.referenceRecordedAt || item.date,
            amount: cropItem.amount,
            originalAmount: cropItem.originalAmount,
            cropId: cropItem.id,
            type: 'TRANSPLANT',
            typeLabel: 'Transplanting',
          });
        }
      }

      const transplantedTotal = (stats.localTransplantedCount || 0) + (stats.externalTransplantedCount || 0);
      const dailyHarvest = harvestWeek.amount / 7;

      const sowReminders = enabledCropsRes.data.map(crop => {
        const row = cropPipeline.find(item => item.cropId === crop.id) || {
          inPropagation: 0,
          inProduction: 0,
          harvestWeek: 0,
        };
        const dailyCropHarvest = row.harvestWeek / 7;
        const demand = dailyCropHarvest > 0 ? dailyCropHarvest : (enabledCropsRes.data.length ? dailyHarvest / enabledCropsRes.data.length : 0);
        if (demand <= 0) {
          return null;
        }
        const leadTime = crop.seedlingDayCount + crop.plantingDayCount;
        const targetWip = demand * leadTime;
        const currentWip = row.inPropagation + row.inProduction;
        const shortfall = Math.ceil(targetWip - currentWip);
        if (shortfall <= 0) {
          return null;
        }
        const daysCoverage = row.inProduction > 0 ? Math.floor(row.inProduction / demand) : 0;
        return {
          key: crop.id,
          cropName: crop.name,
          shortfall,
          demand: Math.ceil(demand),
          daysCoverage,
          nextSowBy: moment().format('D/M'),
        };
      }).filter(Boolean);

      setDashboardExtras({
        harvestWeek,
        yieldPerHead: harvestWeek.amount > 0 && harvestWeek.weight > 0
          ? (harvestWeek.weight / harvestWeek.amount).toFixed(1)
          : null,
        propagationGapPct: formatPercent(stats.inPropagationCount, stats.sownCount),
        productionGapPct: formatPercent(stats.inProductionCount, transplantedTotal),
        cropPipeline,
        todayActions,
        leftoverActions,
        sowReminders,
      });

      const siteStationsRes = await ToolRequest.request('GET', `/v1/farm-site/${site.id}/station`);

      const stations = [];
      for (let siteStation of siteStationsRes.data) {
        const station = await ToolRequest.request('GET', `/v1/station/${siteStation.stationId}`);
        stations.push(station);
      }
      stations.sort((a, b) => {
        if (a.displayName < b.displayName) {
          return -1;
        } else if (a.displayName > b.displayName) {
          return 1;
        }
        return 0;
      });
      setStations(stations);

      setLoaded(true);
    })();
  }, [cardKey, site.id, site.companyId]);

  const renderSummaryCrop = (item, crop, type, typeLabel, targetField) => {
    const daysElapsed = ToolMath.daysFromNowNumber(item.date);
    const targetDays = crops[crop.id]?.[targetField];
    const isLeftover = targetDays != null && daysElapsed >= targetDays;
    const status = getStageStatus(daysElapsed, targetDays);
    let statusTag;
    let statusTip;
    if (isLeftover) {
      statusTag = { tag: 'Leftover', color: 'orange' };
      statusTip = type === 'SOW'
        ? 'Past propagation days — remaining after transplant; often missing wastage'
        : 'Past grow days — remaining after harvest; often missing wastage';
    } else {
      statusTag = status;
      statusTip = type === 'SOW' ? 'Days until transplant target' : 'Days until harvest target';
    }

    return (
      <List.Item
        key={crop.id}
        className="clickable"
        onClick={() => {
          setDetailModal({
            key: Math.random().toString(),
            visible: true,
            siteId: site.id,
            cropId: crop.id,
            cropName: crops[crop.id]?.name,
            typeLabel,
            type,
            date: item.date,
          });
        }}
      >
        <List.Item.Meta
          title={
            crops[crop.id] ? (
              <>
                <div style={{marginBottom: 4}}>
                  <CropNameBadge name={crops[crop.id].name} />
                  <Tooltip title={statusTip}>
                    <Tag color={statusTag.color} style={{ marginLeft: 8 }}>{statusTag.tag}</Tag>
                  </Tooltip>
                </div>
                <Tooltip title="Progress toward target days since this batch date">
                  <span><DayCountDisplay value={daysElapsed} target={targetDays} /></span>
                </Tooltip>
              </>
            ) : '-'
          }
          description={
            <div style={{textAlign: 'right'}}>
              <Tooltip title={isLeftover && crop.originalAmount > crop.amount
                ? `Unaccounted remainder (${crop.amount} of ${crop.originalAmount})`
                : 'Remaining plants in this batch'}>
                {type === 'SOW' ? <FunnelPlotFilled /> : <SunFilled />}{' '}
                {isLeftover && crop.originalAmount > crop.amount
                  ? `${crop.amount} of ${crop.originalAmount}`
                  : crop.amount}
              </Tooltip>
            </div>
          }
        />
      </List.Item>
    );
  };

  return (
    <Card
      className="card-container"
      title={title || (
        <>
          <Button
            type="link"
            size="small"
            icon={<ReloadOutlined />}
            style={{float: 'right'}}
            onClick={() => setCardKey(Math.random().toString())}
          />
          <a href={`#/farm-site/dashboard/${site.id}`}>
            <HomeOutlined />
            <span style={{marginLeft: 16}}>{site.name}</span>
          </a>
        </>
      )}
    >
      {loaded && dashboardExtras ? (
        <>
          {stations.length > 0 && (
            <List
              bordered
              size="small"
              dataSource={stations}
              renderItem={item => <HomeSiteCardStationListItem station={item} />}
              style={{ marginBottom: 16 }}
              className="small-list-container"
              header={(
                <>
                  {tip('Active Stations', 'Monitoring stations linked to this site')}
                  <Badge count={stations.length} style={{ marginLeft: 8 }} />
                </>
              )}
            />
          )}

          {(stats.inPropagationCount < 0 || stats.inProductionCount < 0) && (
            <Alert
              type="warning"
              showIcon
              message="Pipeline counts are negative — review production records for missing or excess entries."
              style={{ marginBottom: 16 }}
            />
          )}

          {dashboardExtras.leftoverActions.length > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message={tip(
                'Unaccounted leftovers',
                'Overdue remaining plants — usually missing wastage after transplant/harvest',
              )}
              description={(
                <List
                  size="small"
                  dataSource={dashboardExtras.leftoverActions}
                  renderItem={item => (
                    <List.Item
                      className="clickable"
                      style={{ padding: '4px 0' }}
                      onClick={() => {
                        setDetailModal({
                          key: Math.random().toString(),
                          visible: true,
                          siteId: site.id,
                          cropId: item.cropId,
                          cropName: item.cropName,
                          typeLabel: item.typeLabel,
                          type: item.type,
                          date: item.date,
                        });
                      }}
                    >
                      <Tag color="orange">{item.stageLabel}</Tag>
                      <strong style={{ marginLeft: 8 }}><CropNameBadge name={item.cropName} /></strong>
                      <span style={{ marginLeft: 8 }}>{item.date}</span>
                      <span style={{ float: 'right' }}>
                        {item.amount} of {item.originalAmount} left
                        <Button
                          size="small"
                          type="link"
                          onClick={(e) => {
                            e.stopPropagation();
                            history.push(wasteLogPath(dashboardExtras.leftoverActions, item));
                          }}
                        >
                          Log wastage
                        </Button>
                      </span>
                    </List.Item>
                  )}
                />
              )}
            />
          )}

          {dashboardExtras.sowReminders.length > 0 && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message={tip('Sowing reminders', 'Suggested sowing to match recent harvest rate')}
              description={(
                <List
                  size="small"
                  dataSource={dashboardExtras.sowReminders}
                  renderItem={item => (
                    <List.Item style={{ padding: '4px 0' }}>
                      <strong><CropNameBadge name={item.cropName} /></strong>: sow ~{item.shortfall} plants by {item.nextSowBy}
                      {' '}(~{item.demand}/day demand{item.daysCoverage ? `, ${item.daysCoverage}d production left` : ''})
                    </List.Item>
                  )}
                />
              )}
            />
          )}

          <Row style={{ marginBottom: 16 }}>
            <Col span={24}>
              <Space wrap>
                {tip('Quick log:', 'Add a production record for this site')}
                <Tooltip title="Log new seeds sown">
                  <Button size="small" icon={<FunnelPlotFilled />} style={{ color: '#42A5F5', borderColor: '#42A5F5' }} onClick={() => history.push(quickLogPath('sow'))}>Sowing</Button>
                </Tooltip>
                <Tooltip title="Log seedlings moved to production">
                  <Button size="small" icon={<SwapOutlined />} style={{ color: '#4CAF50', borderColor: '#4CAF50' }} onClick={() => history.push(quickLogPath('transplant'))}>Transplanting</Button>
                </Tooltip>
                <Tooltip title="Log plants harvested">
                  <Button size="small" icon={<ShoppingOutlined />} style={{ color: '#FF9800', borderColor: '#FF9800' }} onClick={() => history.push(quickLogPath('harvest'))}>Harvesting</Button>
                </Tooltip>
                <Tooltip title="Log plants lost or discarded">
                  <Button size="small" icon={<DeleteFilled />} onClick={() => history.push(quickLogPath('waste'))}>Wastage</Button>
                </Tooltip>
              </Space>
            </Col>
          </Row>

          {dashboardExtras.todayActions.length > 0 && (
            <List
              className="small-list-container"
              bordered
              size="small"
              style={{ marginBottom: 16 }}
              header={(
                <>
                  {tip("Today's Actions", 'Batches past target days — transplant or harvest now')}
                  <Badge count={dashboardExtras.todayActions.length} style={{ marginLeft: 8 }} />
                </>
              )}
              dataSource={dashboardExtras.todayActions}
              renderItem={item => (
                <List.Item
                  className="clickable"
                  onClick={() => {
                    setDetailModal({
                      key: Math.random().toString(),
                      visible: true,
                      siteId: site.id,
                      cropId: item.cropId,
                      cropName: item.cropName,
                      typeLabel: item.action === 'Transplant' ? 'Sowing' : 'Transplanting',
                      type: item.action === 'Transplant' ? 'SOW' : 'TRANSPLANT',
                      date: item.date,
                    });
                  }}
                >
                  <Tag color="green">{item.action}</Tag>
                  <strong style={{ marginLeft: 8 }}><CropNameBadge name={item.cropName} /></strong>
                  <span style={{ marginLeft: 8 }}>{item.date}</span>
                  <span style={{ float: 'right' }}>{item.amount} plants</span>
                </List.Item>
              )}
            />
          )}

          <HomeSiteCardPipelineVisual
            stats={stats}
            harvestWeekAmount={dashboardExtras.harvestWeek.amount}
            harvestWeekWeight={dashboardExtras.harvestWeek.weight}
            cropPipeline={dashboardExtras.cropPipeline}
            crops={crops}
            onQuickLog={(action) => history.push(quickLogPath(action))}
          />

          <Row>
            <Col span={8}>
              <Statistic title={tip('Enabled Crops', 'Active crop varieties in your catalog')} value={stats.cropCount} />
            </Col>
            <Col span={8}>
              <Statistic title={tip('In Propagation', 'Sown in - local transplanted out - propagation waste')} value={stats.inPropagationCount} />
            </Col>
            <Col span={8}>
              <Statistic title={tip('In Production', 'All transplanted in - harvested out - production waste')} value={stats.inProductionCount} />
            </Col>
          </Row>

          <Row>
            <Col span={8}>
              <Statistic title={tip('Seeds Sown', 'Total plants sown in at this site')} value={stats.sownCount} />
            </Col>
            <Col span={8}>
              <Statistic title={tip('Propagation Gap', 'In propagation / sown in')} value={dashboardExtras.propagationGapPct} />
            </Col>
            <Col span={8}>
              <Statistic title={tip('Production Gap', 'In production / transplanted in')} value={dashboardExtras.productionGapPct} />
            </Col>
          </Row>

          <Row style={{ marginBottom: 16 }}>
            <Col span={8}>
              <Statistic title={tip('Harvested (7 days)', 'Plants harvested in the last 7 days')} value={dashboardExtras.harvestWeek.amount} suffix="plants" />
            </Col>
            <Col span={8}>
              <Statistic
                title={tip('Yield per Head (7 days)', 'Average g / plant (7d harvest)')}
                value={dashboardExtras.yieldPerHead ?? '-'}
                suffix={dashboardExtras.yieldPerHead ? 'g' : undefined}
              />
            </Col>
            <Col span={8}>
              <Statistic title={tip('Last Harvesting', 'Most recent harvest logged')} value={stats.latestHarvestRecordedAt ?
                `${ToolMath.daysFromNow(stats.latestHarvestRecordedAt)} (${moment(stats.latestHarvestRecordedAt).format('D/M')})`
                : '-'} />
            </Col>
          </Row>

          <Row gutter={[8, 8]}>
            <Col xs={24} lg={12}>
              <List
                className="small-list-container"
                header={(
                  <>
                    <Tag color="blue" style={{ marginRight: 8 }}>Step 2</Tag>
                    {tip('Propagation Summary', 'Sowing batches grouped by date')}
                    <Badge count={propagationSummary.length} style={{ marginLeft: 8 }} />
                  </>
                )}
                bordered
                size="small"
                dataSource={propagationSummary}
                renderItem={item => (
                  <List.Item>
                    <List.Item.Meta
                      title={`${item.date} (${ToolMath.daysFromNow(item.date)})`}
                      description={(
                        <List
                          size="small"
                          dataSource={item.crops}
                          renderItem={crop => renderSummaryCrop(item, crop, 'SOW', 'Sowing', 'seedlingDayCount')}
                        />
                      )}
                    />
                  </List.Item>
                )}
              />
            </Col>
            <Col xs={24} lg={12}>
              <List
                className="small-list-container"
                header={(
                  <>
                    <Tag color="green" style={{ marginRight: 8 }}>Step 4</Tag>
                    {tip('Production Summary', 'Transplant batches grouped by date')}
                    <Badge count={productionSummary.length} style={{ marginLeft: 8 }} />
                  </>
                )}
                bordered
                size="small"
                dataSource={productionSummary}
                renderItem={item => (
                  <List.Item>
                    <List.Item.Meta
                      title={`${item.date} (${ToolMath.daysFromNow(item.date)})`}
                      description={(
                        <List
                          size="small"
                          dataSource={item.crops}
                          renderItem={crop => renderSummaryCrop(item, crop, 'TRANSPLANT', 'Transplanting', 'plantingDayCount')}
                        />
                      )}
                    />
                  </List.Item>
                )}
              />
            </Col>
          </Row>

          {detailModal && (
            <HomeSiteCardProductionDetailModal
              key={detailModal.key}
              visible={detailModal.visible}
              siteId={detailModal.siteId}
              cropId={detailModal.cropId}
              cropName={detailModal.cropName}
              typeLabel={detailModal.typeLabel}
              type={detailModal.type}
              date={detailModal.date}
              onCancel={() => {
                setDetailModal({
                  ...detailModal,
                  visible: false,
                });
              }}
            />
          )}
        </>
      ) : <SpinFull />}
    </Card>
  );
};
